import { AxiosResponse, AxiosError } from "axios";
import { useQuery, QueryObserverResult, UseQueryOptions } from "react-query";

import {
  FetcherQueryKey,
  computeIsQueryEnabled,
} from "../../../services/query";
import { CropsNetworkResponse, CropsDataEnvelope } from "../../../models/crops";

import { useMaybeUserId } from "../userId";
import { useMaybeLocale } from "../locale";

import {
  CropsFetcherParams,
  cropsQueryKeyPrefix,
  computeCropsQueryKey,
  cropsFetcher,
  isDataEqual,
  computeResultOfUseQuery,
  onSuccess,
  onError,
  useHandleLoadingStatus,
} from "./crops.query.helpers";
import { useSetCropsGlobalActivityAndStatus } from "./crops.globalStatus";
import { useMaybeFabricFeatureFlag } from "..";

export { cropsQueryKeyPrefix, computeCropsQueryKey };

// ###################
// ## useCropsQuery ##
// ###################

export type ParamsOfUseCropsQuery = {
  watch?: UseQueryOptions["notifyOnChangeProps"];
  forceDisable?: boolean;
};
export type ResultOfUseCropsQuery = QueryObserverResult<
  CropsDataEnvelope,
  AxiosError
>;
export type UseCropsQuery = (
  params?: ParamsOfUseCropsQuery,
) => ResultOfUseCropsQuery;

/**
 * The returned value of this hook will contain the React Query result
 * of the fetching of the crops data.
 *
 * This hook is only meant for use by the hooks in `./crops.ts`.
 * Do not use this hook outside these hooks as this could couple us to React Query.
 */
export const useCropsQuery: UseCropsQuery = ({ watch, forceDisable } = {}) => {
  const userId = useMaybeUserId();
  const locale = useMaybeLocale();
  const catalogFlag = useMaybeFabricFeatureFlag({
    key: "crops-api",
    ignoreOperationId: true,
  });

  const setCropsGlobalActivityAndStatus = useSetCropsGlobalActivityAndStatus();

  const params: CropsFetcherParams = { userId, locale, catalogFlag };
  const resultOfUseQuery = useQuery<
    AxiosResponse<CropsNetworkResponse>,
    AxiosError,
    AxiosResponse<CropsNetworkResponse>,
    FetcherQueryKey<CropsFetcherParams>
  >(computeCropsQueryKey(params), cropsFetcher, {
    enabled: computeIsQueryEnabled(params, { forceDisable }),
    notifyOnChangeProps: watch || "tracked",
    isDataEqual,
    onSuccess: () => onSuccess({ setCropsGlobalActivityAndStatus }),
    onError: () => onError({ setCropsGlobalActivityAndStatus }),
    // Crops are updated only a few times a year so we can be pretty loose
    // with how often we allow this data to be revalidated without
    // explicit invalidation/revalidation. Setting this to 7 days so it
    // will still periodically be updated when we implement something like hydration.
    staleTime: 604800000, // 7 days
  });

  useHandleLoadingStatus({
    resultOfUseQuery,
    setCropsGlobalActivityAndStatus,
  });

  return computeResultOfUseQuery(resultOfUseQuery);
};
